import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
// eslint-disable-next-line @typescript-eslint/camelcase
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { AfterViewInit, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { CurrencyService } from '@app/shared/services/currency/currency.service';
import { cloneDeep } from 'lodash';
import { ChartHelper } from '@app/shared/components/charts/helpers/chart.helper';
import {
  ChartDataInterface,
  ChartDataType,
  ChartLegendPosition, ChartTheme,
} from '@app/shared/components/charts/interfaces/chart.interface';

@Component({
  selector: 'generic-mixed-chart',
  templateUrl: './generic-mixed-chart.component.html',
  styleUrls: ['./generic-mixed-chart.component.scss'],
})
export class GenericMixedChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() chartId = 'mixedChart';
  @Input() title: string;
  @Input() lineAxisTitle: string;
  @Input() columnAxisTitle: string;
  @Input() bullets = false;
  @Input() horizontalGrid = false;
  @Input() additionalLabelsEnabled = false;

  @Input() set columnData(value: ChartDataInterface[]) {
    if (typeof value !== 'undefined') {
      this.columnChartData = value.map(item => ({ ...item }));
    }
  }

  @Input() set lineData(value: ChartDataInterface[]) {
    if (typeof value !== 'undefined') {
      this.lineChartData = value.map(item => ({ ...item }));
    }
  }

  @Input() columnSeries: string[] = [];
  @Input() lineSeries: string[] = [];
  @Input() lineDataType: ChartDataType = ChartDataType.NUMBER;
  @Input() columnDataType: ChartDataType = ChartDataType.NUMBER;
  @Input() legend = false;
  @Input() legendPosition: ChartLegendPosition = 'bottom';
  @Input() chartHeight = '600px';
  @Input() disableValueLabels = true;
  @Input() theme: ChartTheme = ChartTheme.WHITE;

  private chart: am4charts.XYChart;
  private columnNegativeValuesEnabled = false;
  private lineNegativeValuesEnabled = false;
  private availableDataTypes = ChartDataType;
  private readonly colorsList = [
    am4core.color('#38B3B7'),
    am4core.color('#C0BB48'),
    am4core.color('#6F81DC'),
    am4core.color('#A0D468'),
    am4core.color('#FFCE54'),
    am4core.color('#9B72FF'),
    am4core.color('#643cbb'),
    am4core.color('#a33408'),
    am4core.color('#ffec17'),
    am4core.color('#5f9bd9'),
    am4core.color('#483334'),
    am4core.color('#8f8559'),
    am4core.color('#1a8777'),
    am4core.color('#dae0f0'),
  ];
  private columnChartData: ChartDataInterface[] = [];
  private lineChartData: ChartDataInterface[] = [];

  constructor(
    private readonly zone: NgZone,
    private readonly currencyService: CurrencyService,
    private readonly decimalPipe: DecimalPipe,
    private readonly chartHelper: ChartHelper,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const columnDataChange = changes['columnData'];
    const lineDataChange = changes['lineData'];
    if (columnDataChange && typeof columnDataChange.previousValue !== 'undefined') {
      this.checkColumnPercentage();
      this.loadChart();
    }
    if (lineDataChange && typeof lineDataChange.previousValue !== 'undefined') {
      this.checkLinePercentage();
      this.loadChart();
    }
  }

  ngAfterViewInit(): void {
    this.checkLinePercentage();
    this.checkColumnPercentage();
    this.loadChart();
  }

  ngOnDestroy(): void {
    this.disposeChart();
  }


  private getColumnSeriesLabelRotation(): number {
    return this.columnChartData.length > 6 ? 270 : 0;
  }

  private checkIfChartHasNegativeValues(): void {
    this.columnChartData.forEach(element => {
      if (element.values.some(item => item < 0)) {
        this.columnNegativeValuesEnabled = true;
      }
    });
    this.lineChartData.forEach(element => {
      if (element.values.some(item => item < 0)) {
        this.lineNegativeValuesEnabled = true;
      }
    });
  }

  private createChart(): void {
    am4core.useTheme(am4themes_animated);
    const chart = am4core.create(this.chartId, am4charts.XYChart);
    const barChartData = this.chartHelper.prepareChartDataWithExtendedLabels(this.columnChartData, this.columnSeries);
    const lineChartData = this.chartHelper.prepareChartDataWithExtendedLabels(this.lineChartData, this.lineSeries);
    chart.data = [...barChartData, ...lineChartData];
    chart.paddingRight = 0;
    chart.paddingLeft = 0;
    chart.colors.list = this.colorsList;

    const title = chart.titles.create();
    title.text = this.title;
    title.fontSize = 20;
    title.marginBottom = 10;
    title.marginTop = 10;
    title.align = 'center';

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'seriesName';
    if (this.additionalLabelsEnabled) {
      categoryAxis.renderer.labels.template.adapter.add('textOutput', (text, label) => {
        if (text) {
          return text.replace(/-\d+$/, '');
        }
      });
    }
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.renderer.labels.template.rotation = this.getColumnSeriesLabelRotation();
    categoryAxis.renderer.labels.template.verticalCenter = 'middle';
    categoryAxis.renderer.cellStartLocation = 0.1;
    categoryAxis.renderer.cellEndLocation = 0.9;
    categoryAxis.cursorTooltipEnabled = false;
    categoryAxis.renderer.grid.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    categoryAxis.renderer.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);

    const columnAxis = chart.yAxes.push(new am4charts.ValueAxis());
    columnAxis.renderer.grid.template.disabled = true;
    columnAxis.cursorTooltipEnabled = false;
    columnAxis.renderer.grid.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    columnAxis.renderer.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    columnAxis.renderer.baseGrid.stroke = this.chartHelper.getThemeColor(this.theme);
    if (this.lineAxisTitle && this.lineChartData && this.lineChartData.length > 0) {
      columnAxis.title.text = this.lineAxisTitle;
    }

    if (this.columnAxisTitle && this.columnChartData && this.columnChartData.length > 0) {
      columnAxis.title.text = this.columnAxisTitle;
      columnAxis.title.rotation = 270;
      columnAxis.title.align = 'center';
      columnAxis.title.valign = 'middle';
      columnAxis.title.fill =
        this.chartHelper.getThemeColor(this.theme);
    }
    if (this.columnDataType === this.availableDataTypes.PERCENTAGE) {
      columnAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return label.dataItem['value'] + '%';
      });
    }
    if (this.columnDataType === this.availableDataTypes.CURRENCY) {
      columnAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return this.currencyService.currencySymbol + this.decimalPipe.transform(label.dataItem['value'], '1.');
      });
    }
    if (this.columnDataType === this.availableDataTypes.NUMBER) {
      columnAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return this.decimalPipe.transform(label.dataItem['value'], '1.');
      });
    }
    if (!this.columnNegativeValuesEnabled) {
      columnAxis.min = 0;
    }

    const createColumnsSeries = (name: string, index: number): am4charts.ColumnSeries => {
      const columnSeries = chart.series.push(new am4charts.ColumnSeries());
      columnSeries.name = name;
      columnSeries.dataFields.valueY = name;
      columnSeries.dataFields.categoryX = 'seriesName';
      columnSeries.yAxis = columnAxis;
      columnSeries.columns.template.column.cornerRadius(2, 2, 2, 2);
      columnSeries.fillOpacity = 0.9;
      columnSeries.tooltip.autoTextColor = false;
      columnSeries.tooltipText = this.getColumnTooltipTextWithExtendedLabels(index);
      columnSeries.tooltip.label.fill = this.chartHelper.getThemeColor(this.theme);
      const valueLabel = columnSeries.bullets.push(new am4charts.LabelBullet());
      valueLabel.label.truncate = false;
      valueLabel.disabled = true;
      if (this.columnChartData.length < 10) {
        valueLabel.label.text = this.getVerticalLabelText();
        valueLabel.label.fontSize = 16;
        valueLabel.locationY = 0.5;
      }
      return columnSeries;
    };

    this.columnSeries.forEach((seriesName, index) => {
      createColumnsSeries(seriesName, index);
    });

    const lineAxis = chart.yAxes.push(new am4charts.ValueAxis());
    lineAxis.startLocation = 0.5;
    lineAxis.endLocation = 0.5;
    lineAxis.renderer.grid.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    lineAxis.renderer.grid.template.stroke =
      this.chartHelper.getThemeColor(this.theme);
    lineAxis.renderer.grid.template.disabled = !this.horizontalGrid;
    lineAxis.renderer.opposite = true;
    lineAxis.renderer.baseGrid.stroke =
      this.chartHelper.getThemeColor(this.theme);
    if (!this.lineNegativeValuesEnabled) {
      lineAxis.min = 0;
    }
    if (this.lineAxisTitle) {
      lineAxis.title.text = this.lineAxisTitle;
      lineAxis.title.rotation = 270;
      lineAxis.title.align = 'center';
      lineAxis.title.valign = 'middle';
      lineAxis.title.fill =
        this.chartHelper.getThemeColor(this.theme);
    }
    lineAxis.renderer.labels.template.fill =
      this.chartHelper.getThemeColor(this.theme);
    lineAxis.cursorTooltipEnabled = false;
    if (this.lineDataType === this.availableDataTypes.PERCENTAGE) {
      lineAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return label.dataItem['value'] + '%';
      });
    }
    if (this.lineDataType === this.availableDataTypes.CURRENCY) {
      lineAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return this.currencyService.currencySymbol + label.dataItem['value'];
      });
    }

    if (this.lineDataType === this.availableDataTypes.NUMBER) {
      lineAxis.renderer.labels.template.adapter.add('text', (text, label) => {
        return this.decimalPipe.transform(label.dataItem['value'], '1.');
      });
    }

    const createLineSeries = (name: string, index: number): am4charts.LineSeries => {
      const lineSeries = chart.series.push(new am4charts.LineSeries());
      lineSeries.name = name;
      lineSeries.tooltipText = 't';
      lineSeries.tooltip.disabled = false;
      lineSeries.dataFields.categoryX = 'seriesName';
      lineSeries.dataFields.valueY = name;
      lineSeries.tensionX = 0.7;
      lineSeries.tensionY = 1;
      lineSeries.yAxis = lineAxis;
      lineSeries.strokeWidth = 3;
      const interfaceColors = new am4core.InterfaceColorSet();
      if (this.bullets) {
        const bullet = lineSeries.bullets.push(new am4charts.CircleBullet());
        bullet.circle.stroke = interfaceColors.getFor('background');
        bullet.circle.strokeWidth = 0;
      }

      return lineSeries;
    };

    this.lineSeries.forEach((element, index) => {
      createLineSeries(element, index);
    });

    if (this.legend) {
      chart.legend = new am4charts.Legend();
      chart.legend.position = this.legendPosition;
      chart.legend.labels.template.fill = this.chartHelper.getThemeColor(this.theme);
      const markerTemplate = chart.legend.markers.template;
      markerTemplate.width = 21;
      markerTemplate.height = 37;
    }

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = true;
    chart.cursor.behavior = 'none';

    this.chart = chart;
  }

  private getVerticalLabelText(): string {
    if (this.lineDataType === this.availableDataTypes.PERCENTAGE) {
      return '{valueY}%';
    }
    if (this.lineDataType === this.availableDataTypes.CURRENCY) {
      return `${this.currencyService.currencySymbol}{valueY}`;
    }
    return '{valueY}';
  }

  private loadChart(): void {
    am4core.unuseAllThemes();
    this.checkIfChartHasNegativeValues();
    this.zone.runOutsideAngular(() => {
      this.createChart();
    });
  }

  private disposeChart(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
      }
    });
  }

  private getLineTooltipTextWithExtendedLabels(index: number): string {
    if (this.lineDataType === ChartDataType.CURRENCY) {
      return `{label${index}}\n{name}: [bold]${this.currencyService.currencySymbol}{valueY.formatNumber("#.")}[/]`;
    }
    if (this.lineDataType === ChartDataType.PERCENTAGE) {
      return `{label${index}}\n{name}: [bold]{valueY.formatNumber("#.#")}%[/]`;
    }
    return `{label${index}}\n{name}: [bold]{valueY.formatNumber("#.")}[/]`;
  }

  private getColumnTooltipTextWithExtendedLabels(index: number): string {
    if (this.columnDataType === ChartDataType.CURRENCY) {
      return `{label${index}}\n{name}: [bold]${this.currencyService.currencySymbol}{valueY.formatNumber("#.")}[/]`;
    }
    if (this.columnDataType === ChartDataType.PERCENTAGE) {
      return `{label${index}}\n{name}: [bold]{valueY.formatNumber("#.#")}%[/]`;
    }
    return `{label${index}}\n{name}: [bold]{valueY.formatNumber("#.")}[/]`;
  }

  private checkColumnPercentage(): void {
    const columnChartData = cloneDeep(this.columnChartData);
    if (this.columnDataType === this.availableDataTypes.PERCENTAGE) {
      columnChartData.forEach((item, index) => {
        const values = item.values.map(value => Number((value * 100).toFixed(2)));
        columnChartData[index].values = values;
      });
      this.columnChartData = columnChartData;
    }
  }

  private checkLinePercentage(): void {
    const lineChartData = cloneDeep(this.lineChartData);
    if (this.lineDataType === this.availableDataTypes.PERCENTAGE) {
      lineChartData.forEach((item, index) => {
        const values = item.values.map(value => Number((value * 100).toFixed(2)));
        lineChartData[index].values = values;
      });
      this.lineChartData = lineChartData;
    }
  }
}
